/**
 * The layout as this session is showing it, which is not the layout as saved.
 *
 * Two questions live here, and they are the same kind of question: where does
 * the toolbar strip hang, and what does the grid look like with the screener
 * hidden. Neither answer may be written back — whether a panel is showing is
 * view state (D8), and persisting it would reopen whatever happened to be on
 * screen when a workspace was last saved.
 *
 * Pure, and here rather than inline in `App`, for the usual reason: `App`
 * reaches SciChart through `SectionGrid` and cannot be rendered in a test.
 */

import type { Layout, Section } from "@qufolio/model";

import { screenerPlacement, type ScreenerPlacement } from "./layout-templates";

/** The tracks a section covers on one axis, as a half-open range. */
const spanOf = (section: Section, vertical: boolean): [number, number] => {
  const start = vertical ? section.row : section.column;
  return [start, start + ((vertical ? section.rowSpan : section.columnSpan) ?? 1)];
};

/** Which edge of {@link ToolbarAnchor.column} the strip's own edge lands on. */
export type ToolbarSide = "left" | "right";

/**
 * The strip's two parts when the screener is docked above or below the charts:
 * the rows beside the dock, and the rows beside the chart region. Both
 * half-open, contract rows, and together they are {@link ToolbarAnchor.rows}.
 */
export interface ToolbarSplit {
  /** Beside the screener's cell — the dock keys go here. */
  dock: [number, number];
  /** Beside the charts — chart type, indicators and the drawing tools. */
  chart: [number, number];
}

export interface ToolbarAnchor {
  /** The chart region's edge column, in contract coordinates. */
  column: number;
  side: ToolbarSide;
  /**
   * The contract rows the strip spans, half-open — the chart region's, or the
   * whole grid's when it is {@link split}.
   */
  rows: [number, number];
  /** Present only with the screener docked top or bottom (owner, 2026-10-07). */
  split?: ToolbarSplit;
}

/** The half-open range a set of sections covers on one axis. */
const rangeOf = (sections: readonly Section[], vertical: boolean): [number, number] => [
  Math.min(...sections.map((s) => spanOf(s, vertical)[0])),
  Math.max(...sections.map((s) => spanOf(s, vertical)[1])),
];

/**
 * Where the vertical toolbar strip hangs: a column of the chart region, an edge
 * of that column, the rows it runs down — and, with the screener above or
 * below, where it divides.
 *
 * **The strip belongs to the charts and travels with them.** Chart type and
 * indicators act on the chart on the other side of that seam, so it is derived
 * from the *chart region* and never from the screener's own column — which is
 * what this read before, and which put the chart's buttons wherever the screener
 * happened to be docked.
 *
 * | screener | strip | rows |
 * |---|---|---|
 * | right | the chart region's right edge | the chart region's |
 * | left | its left edge | the chart region's |
 * | top | its left edge | the whole grid, split: dock keys, then chart keys |
 * | bottom | its left edge | the whole grid, split: chart keys, then dock keys |
 *
 * With the screener on the left or the right the strip sits on the seam between
 * the two, which is the boundary the controls describe, and every key is
 * together in one column. Above or below there is no vertical seam to sit on,
 * so it stays on the left — a fixed home beats a strip that changes sides for
 * reasons the user cannot see.
 *
 * **Top and bottom run the full height, in two parts** (owner, 2026-10-07,
 * "option C"). It used to span the chart rows only, so that a screener docked on
 * top was not straddled by chart buttons — but that put the screener's own keys
 * (Split, data, screener, conditions, Community) beside the *charts*, a whole
 * panel away from what they open. Now each key sits beside what it acts on: the
 * dock keys down the screener's rows, chart type, indicators and the drawing
 * tools down the chart rows. The boundary between the parts is the row seam
 * itself, so a divider drag moves both together. The screener gives up the
 * strip's width to get it.
 *
 * `placement` is a parameter rather than a lookup so the caller can hand over
 * the *saved* layout's side while passing the layout with the screener hidden:
 * a hidden screener is not on any side, and re-deriving would jump the strip
 * across the charts the moment the panel went away. With it hidden there is no
 * screener row to sit beside, so there is no split either — the chart rows,
 * exactly as before.
 *
 * A layout with no charts answers column 0 on the right: there is no chart
 * region to sit beside, and the strip still carries the screener's own buttons,
 * so it anchors to the first seam instead of off the left edge of the grid.
 */
export function toolbarAnchor(
  layout: Layout,
  placement: ScreenerPlacement = screenerPlacement(layout),
): ToolbarAnchor {
  const charts = layout.sections.filter((section) => section.kind === "chartGroup");
  if (!charts.length) {
    return { column: 0, side: "right", rows: [0, Math.max(1, layout.rows)] };
  }
  const side: ToolbarSide = placement === "right" ? "right" : "left";
  const column =
    side === "right"
      ? Math.max(...charts.map((s) => spanOf(s, false)[1] - 1))
      : Math.min(...charts.map((s) => s.column));
  const chartRows = rangeOf(charts, true);

  const docked = layout.sections.filter((section) => section.kind !== "chartGroup");
  if ((placement === "top" || placement === "bottom") && docked.length) {
    const dockRows = rangeOf(docked, true);
    // Only when the two really are stacked — a hand-built layout whose rows
    // overlap has no single seam for the parts to meet at, and keeps one strip.
    const stacked = placement === "top" ? dockRows[1] <= chartRows[0] : chartRows[1] <= dockRows[0];
    if (stacked) {
      return {
        side,
        column,
        rows: [Math.min(dockRows[0], chartRows[0]), Math.max(dockRows[1], chartRows[1])],
        split: { dock: dockRows, chart: chartRows },
      };
    }
  }
  return { side, column, rows: chartRows };
}

/**
 * The same layout with every screener removed and the rows and columns they had
 * to themselves collapsed, so the space goes back to the charts rather than
 * leaving a gap where the grid was.
 *
 * Both axes, because a screener docked on top or below owns a *row* — a
 * column-only version left a full-width empty band across the layout, which
 * reads as the screener having failed to render rather than as having been
 * hidden.
 *
 * Tracks are dropped by *coverage*, not by counting screeners: a track is
 * removed only when nothing else is drawn in it. `applyTemplate` already gives
 * each non-chart section a track of its own, so in practice that is one per
 * screener — but a layout hand-built with a chart sharing the screener's column
 * would otherwise lose the chart's track and take every section beyond it along
 * too. That same rule is what makes the surviving spans safe to leave alone: a
 * section spanning a track covers it, so no dropped track is inside one.
 *
 * A screener-only layout comes back untouched. There is nothing to give the
 * space to, and a zero-column grid renders as nothing at all — which reads as
 * the app having crashed rather than as a panel having been hidden.
 */
export function withoutScreener(layout: Layout): Layout {
  const rest = layout.sections.filter((s) => s.kind !== "screener");
  if (rest.length === layout.sections.length || !rest.length) return layout;

  const columns = hiddenColumns(layout);
  const rows = hiddenRows(layout);
  if (!columns.length && !rows.length) return { ...layout, sections: rest };

  /** How far a track slides once the dropped ones before it are gone. */
  const shift = (dropped: number[]) => (track: number) =>
    track - dropped.filter((d) => d < track).length;
  const shiftColumn = shift(columns);
  const shiftRow = shift(rows);

  return {
    ...layout,
    rows: layout.rows - rows.length,
    columns: layout.columns - columns.length,
    ...(layout.rowSizes
      ? { rowSizes: layout.rowSizes.filter((_, i) => !rows.includes(i)) }
      : {}),
    ...(layout.columnSizes
      ? { columnSizes: layout.columnSizes.filter((_, i) => !columns.includes(i)) }
      : {}),
    sections: rest.map((section) => ({
      ...section,
      row: shiftRow(section.row),
      column: shiftColumn(section.column),
    })),
  };
}

/**
 * The layout with one section in it, filling the whole grid — a chart put full
 * screen.
 *
 * View state like {@link withoutScreener}, and for the same reason never written
 * back: full screen is how this session is looking at the workspace, not a
 * change to it. The section keeps its `id`, so `SectionGrid` keeps the same
 * mounted chart and going in or out costs a resize, not a WebGL teardown.
 *
 * The saved sizes are dropped rather than filtered: one track needs none, and a
 * stale `columnSizes` of two entries against a one-column grid would draw an
 * empty second track. A missing id hands the layout back untouched.
 */
export function onlySection(layout: Layout, sectionId: string): Layout {
  const section = layout.sections.find((s) => s.id === sectionId);
  if (!section) return layout;
  const { rowSizes: _r, columnSizes: _c, ...rest } = layout;
  return {
    ...rest,
    rows: 1,
    columns: 1,
    sections: [{ ...section, row: 0, column: 0, rowSpan: 1, columnSpan: 1 }],
  };
}

/** The tracks on one axis that a screener has entirely to itself, ascending. */
function hiddenTracks(layout: Layout, vertical: boolean): number[] {
  const screeners = layout.sections.filter((s) => s.kind === "screener");
  const rest = layout.sections.filter((s) => s.kind !== "screener");
  if (!screeners.length || !rest.length) return [];

  const covered = new Set<number>();
  for (const section of rest) {
    const [from, to] = spanOf(section, vertical);
    for (let t = from; t < to; t++) covered.add(t);
  }

  const dropped = new Set<number>();
  for (const section of screeners) {
    const [from, to] = spanOf(section, vertical);
    for (let t = from; t < to; t++) if (!covered.has(t)) dropped.add(t);
  }
  return [...dropped].sort((a, b) => a - b);
}

/** The columns {@link withoutScreener} collapses — a screener docked left or right. */
export const hiddenColumns = (layout: Layout): number[] => hiddenTracks(layout, false);

/** The rows it collapses — a screener docked top or bottom. */
export const hiddenRows = (layout: Layout): number[] => hiddenTracks(layout, true);

/**
 * Widen a drag made while the screener was hidden back to the saved layout's
 * track count.
 *
 * Without this, dragging the divider between two charts with the screener
 * hidden would save a size list one entry short — and the screener would come
 * back with the size of whatever track inherited its index, or none at all. The
 * hidden tracks keep the size they were saved with, since the drag could not
 * have moved a seam that was not on screen.
 */
function restoreSizes(
  hidden: number[],
  saved: readonly string[] | undefined,
  shown: readonly string[],
  count: number,
): string[] {
  if (!hidden.length) return [...shown];

  const out: string[] = [];
  let next = 0;
  for (let track = 0; track < count; track++) {
    out.push(hidden.includes(track) ? (saved?.[track] ?? "1fr") : (shown[next++] ?? "1fr"));
  }
  return out;
}

/** {@link restoreSizes} for a column drag. */
export const restoreColumnSizes = (layout: Layout, shown: readonly string[]): string[] =>
  restoreSizes(hiddenColumns(layout), layout.columnSizes, shown, layout.columns);

/**
 * {@link restoreSizes} for a row drag — the seam between the charts and a
 * screener docked top or bottom runs horizontally, so a row drag is now as
 * capable of being made against a collapsed grid as a column drag was.
 */
export const restoreRowSizes = (layout: Layout, shown: readonly string[]): string[] =>
  restoreSizes(hiddenRows(layout), layout.rowSizes, shown, layout.rows);
